import { useEffect, useState } from 'react'

/**
 * Apply Telegram themeParams to CSS variables on :root.
 * Standalone function so main.tsx can call it before React mounts
 * (avoids a frame of unthemed paint).
 */
export function applyTelegramTheme(params: TelegramWebAppThemeParams): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const set = (key: string, value: string | undefined): void => {
    if (value) root.style.setProperty(key, value)
  }
  set('--tg-bg-color', params.bg_color)
  set('--tg-text-color', params.text_color)
  set('--tg-hint-color', params.hint_color)
  set('--tg-link-color', params.link_color)
  set('--tg-button-color', params.button_color)
  set('--tg-button-text-color', params.button_text_color)
  set('--tg-secondary-bg-color', params.secondary_bg_color)
  set('--tg-header-bg-color', params.header_bg_color)

  // Sync data-theme attribute based on perceived brightness of bg_color.
  if (params.bg_color) {
    const isDark = isColorDark(params.bg_color)
    root.setAttribute('data-color-scheme', isDark ? 'dark' : 'light')
  }
}

function isColorDark(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m || !m[1]) return false
  const v = parseInt(m[1], 16)
  const r = (v >> 16) & 0xff
  const g = (v >> 8) & 0xff
  const b = v & 0xff
  // Perceived luminance.
  const lum = 0.299 * r + 0.587 * g + 0.114 * b
  return lum < 128
}

/**
 * React hook variant — useful for components that want to react to theme
 * changes (e.g. re-render Pixi colors when themeChanged fires).
 */
export function useTelegramTheme(): TelegramWebAppThemeParams {
  const [params, setParams] = useState<TelegramWebAppThemeParams>(() => {
    return window.Telegram?.WebApp?.themeParams ?? {}
  })

  useEffect(() => {
    const tg = window.Telegram?.WebApp
    if (!tg) return
    const onChange = (): void => {
      const next = tg.themeParams ?? {}
      setParams(next)
      applyTelegramTheme(next)
    }
    try {
      tg.onEvent('themeChanged', onChange)
    } catch {
      /* no-op */
    }
    return () => {
      try {
        tg.offEvent('themeChanged', onChange)
      } catch {
        /* no-op */
      }
    }
  }, [])

  return params
}
